import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { unifyColors, unifyBlueGrey, unifyBlue, unifyGreen, unifyGrey, unifyPurple, unifyRed, unifyTeal, unifyYellow } from './colors';

export const UnifyPreset = definePreset(Aura, {
  // 1) PRIMITIVE TIER — register the brand ramps as PrimeVue primitive tokens so the
  // semantic + component tiers can reference them via {blue.400} etc. (single source of truth).
  primitive: {
    blue: unifyBlue,
    teal: unifyTeal,
    red: unifyRed,
    green: unifyGreen,
    yellow: unifyYellow,
    purple: unifyPurple,
    grey: unifyGrey,
  },
  // 2) SEMANTIC TIER — primary ramp + matrix-aligned aliases. Setting these once means most
  // components resolve correct colors without per-component overrides.
  semantic: {
    // The unify "blue" ramp is a teal-navy ramp: primary.400 = #2B6B7B (Type/secondary, teal
    // links on white), primary.700 = #003341 (Type/primary + focus ring).
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
    // Single mode-aware focus ring (WCAG 2.4.7/2.4.11): one outline whose color flips
    // navy<->teal via --color-focus-ring (defined in styles.scss under .dark/.unify-dark-mode).
    focusRing: {
      width: 'var(--focus-ring-width)',
      style: 'solid',
      color: 'var(--color-focus-ring)',
      offset: 'var(--focus-ring-offset)',
      shadow: 'none',
    },
    // Aura sets form-field focusRing width:0 and recolors the border instead; switch them
    // to the same single ring so inputs/selects match buttons.
    formField: {
      focusRing: {
        width: 'var(--focus-ring-width)',
        style: 'solid',
        color: 'var(--color-focus-ring)',
        offset: 'var(--focus-ring-offset)',
        shadow: 'none',
      },
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.400}', // #2B6B7B — links, active states, checked controls
          contrastColor: unifyColors.white,
          hoverColor: '{primary.500}', // #004D61
          activeColor: '{primary.600}', // #004051
        },
        text: {
          color: '{primary.700}', // #003341 Type/primary
          mutedColor: '{grey.600}', // #525252 Type/tertiary
        },
        surface: {
          0: unifyColors.white, // toolbar background
          50: unifyGrey[50],
          100: unifyGrey[100],
          200: unifyGrey[200],
          300: unifyGrey[300],
          400: unifyGrey[400],
          500: unifyGrey[500],
          600: unifyGrey[600],
          700: unifyGrey[700],
          800: unifyGrey[800],
          900: unifyGrey[900],
          950: unifyGrey[950],
        },
        // Stop the on-focus border recolor (--p-*-focus-border-color); the ring is the focus cue.
        formField: {
          focusBorderColor: '{form.field.border.color}',
          // 1.4.11 non-text contrast. Aura's defaults all failed; these are the lightest
          // steps on our scale that clear 3:1 on both surfaces inputs sit on.
          borderColor: '{grey.500}', // 4.74:1 — grey.400 fails at 2.52:1
          hoverBorderColor: '{grey.600}', // darker than rest, so hover reads as emphasis not fade
          invalidBorderColor: '{red.600}', // 4.77:1 — the inherited red.400 was 2.87:1
          // disabledBackground left alone: 1.4.11 exempts inactive components.
        },
      },
      dark: {
        surface: {
          0: unifyColors.white, // text
          50: unifyColors.mainGreen,
          100: unifyTeal[100],
          200: unifyTeal[200],
          300: unifyTeal[300],
          400: unifyTeal[400],
          500: unifyColors.white, // icons
          600: unifyColors.mainTeal,
          700: unifyTeal[500], // avatar, borders
          800: unifyColors.mainTeal,
          900: unifyTeal[400], // toolbar background
          950: unifyColors.mainTeal,
        },
        formField: {
          focusBorderColor: '{form.field.border.color}',
        },
      },
    },
  },
  components: {
    avatar: {
      colorScheme: {
        light: {
          root: {
            background: 'transparent',
            color: unifyYellow[600],
          },
        },
        dark: {
          root: {
            background: unifyColors.curiousBlue,
            color: unifyColors.white,
          },
        },
      },
    },
    button: {
      css: `
        .p-button-primary {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
      `,
      root: {
        borderRadius: '{border.radius.lg}', // 8px — match the Figma button shape (radius/lg); Aura defaults to md/6px
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: 'var(--color-surface-brand)', // teal/200 light → teal/600 dark/inverse (Figma Surface/brand/default)
              color: unifyBlue[700], // navy text, both modes (Figma Type/static)
              hoverColor: unifyBlue[700],
              hoverBackground: 'var(--color-surface-brand-hover)',
              hoverBorderColor: unifyTeal[400],
              borderColor: unifyTeal[400], // teal/400 border, both modes
              focusRing: { color: 'var(--color-focus-ring)' },
            },
            secondary: {
              background: unifyColors.white,
              color: unifyBlue[700],
              hoverBackground: unifyColors.white,
              hoverBorderColor: unifyGrey[200],
              borderColor: unifyGrey[200],
              focusRing: { color: 'var(--color-focus-ring)' },
            },
            // Override Aura's per-severity ring colors so every button uses the single token.
            warn: {
              focusRing: { color: 'var(--color-focus-ring)' },
            },
            danger: {
              // white text clears 4.5:1 (4.77:1) on red/600; Aura's default red/500 (#F54E4E) is 3.43:1 — fails 1.4.3.
              background: unifyRed[600],
              borderColor: unifyRed[600],
              hoverBackground: unifyRed[700],
              hoverBorderColor: unifyRed[700],
              focusRing: { color: 'var(--color-focus-ring)' },
            },
          },
          link: {
            color: '{primary.400}', // #2B6B7B teal link on white (Track B)
            hoverColor: '{primary.500}',
          },
        },
        dark: {
          // Re-declare the brand-fill tokens under .unify-dark-mode so var(--color-surface-brand)
          // re-resolves to teal/600 here (PrimeVue bakes the :root value otherwise); navy text +
          // teal/400 border inherit from light, unchanged across modes. (Was: mainRed solid.)
          root: {
            primary: {
              background: 'var(--color-surface-brand)',
              hoverBackground: 'var(--color-surface-brand-hover)',
            },
          },
          link: {
            color: unifyColors.mainRed,
            hoverColor: unifyRed[400],
          },
        },
      },
    },
    // SelectButton / SplitButton share the button family's 8px radius (Figma radius/lg);
    // Aura defaults them to the form-field radius (6px).
    selectbutton: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
    },
    splitbutton: {
      root: {
        borderRadius: '{border.radius.lg}',
      },
    },
    card: {
      colorScheme: {
        light: {
          root: {
            background: unifyColors.mainGrey,
            color: '{primary.400}', // #2B6B7B (was failing mainTeal #338498, 4.29:1)
            shadow: '1px 4px 8px 0 rgba(0, 0, 0, 0.51)',
          },
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: 'unifyColors.white',
          },
          subtitle: {
            color: 'unifyColors.white',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        light: {
          row: {
            color: unifyBlue[700],
          },
          root: {
            borderColor: 'transparent',
          },
          headerCell: {
            background: unifyGrey[100],
            color: unifyColors.type.primary,
          },
        },
        dark: {
          root: {
            borderColor: unifyBlueGrey[200],
          },
          row: {
            background: unifyBlueGrey[500],
          },
          footer: {
            background: unifyBlueGrey[500],
          },
          headerCell: {
            background: unifyTeal[400],
          },
          footerCell: {
            background: unifyBlueGrey[500],
          },
        },
      },
    },
    dialog: {
      colorScheme: {
        light: {
          root: {},
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
            borderColor: unifyColors.white,
          },
        },
      },
    },
    datepicker: {
      // The focus ring PrimeVue draws on .p-datepicker-input alone gets its outline-offset pushed
      // into the adjoining .p-datepicker-dropdown button, which paints over it (later in DOM,
      // same stacking context) and cuts the ring off at that seam. Promote the focused input into
      // its own positive stacking level so its outline paints above the (non-positioned) button
      // instead of underneath it, while keeping the ring's footprint to just the input.
      css: `
        .p-datepicker-input:focus-visible {
          position: relative;
          z-index: 1;
        }
      `,
    },
    divider: {
      colorScheme: {
        light: {
          root: {},
        },
        dark: {
          root: {
            borderColor: unifyBlueGrey[200],
          },
        },
      },
    },
    floatlabel: {
      colorScheme: {
        light: {},
        dark: {
          root: {
            color: '',
            focusColor: '',
            activeColor: unifyTeal[300],
            invalidColor: '',
            transitionDuration: '',
            positionX: '',
            positionY: '',
            fontWeight: '',
            active: {
              fontSize: '',
              fontWeight: '',
            },
          },
          on: {
            active: {
              background: unifyTeal[500],
            },
          },
        },
      },
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            background: unifyGrey[50],
            disabledBackground: unifyGrey[300],
            disabledColor: unifyGrey[500],
          },
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
            borderColor: unifyBlueGrey[200],
            placeholderColor: unifyBlueGrey[200],
          },
        },
      },
    },
    message: {
      colorScheme: {
        light: {
          error: {
            background: '{red.50}', // #FDDCDC Surface/danger-subtle (matrix 1C)
            borderColor: '{red.100}', // #FCC4C4 decorative border
            color: '{red.700}', // #A33434 icon + title, 6.78:1 (Type/danger)
          },
        },
      },
    },
    menu: {
      colorScheme: {
        light: {
          root: {},
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
          },
          item: {
            focusBackground: unifyTeal[300],
          },
        },
      },
    },
    menubar: {
      colorScheme: {
        light: {
          root: {
            background: unifyBlue[700],
            borderColor: 'transparent',
          },
          submenu: {
            borderColor: 'transparent',
          },
          item: {
            color: unifyColors.white,
            activeBackground: unifyBlue[500],
            activeColor: unifyColors.white,
            focusBackground: unifyBlue[600],
            focusColor: unifyColors.white,
            borderRadius: '9999px',
          },
        },
        dark: {
          root: {
            background: unifyBlue[700],
            borderColor: 'transparent',
          },
          item: {
            color: 'rgb(156, 163, 175)', // text-gray-400
            activeBackground: unifyBlue[600],
            activeColor: unifyColors.white,
            focusBackground: unifyBlue[900],
            focusColor: unifyBlue[300],
            borderRadius: '9999px',
          },
        },
      },
    },
    multiselect: {
      // Same fix as select below: Aura's grey/100 option.focusBackground barely reads on the white
      // overlay, so draw the single focus ring inset on the keyboard-focused option (WCAG 2.4.7).
      css: `
        .p-multiselect-option.p-focus {
          outline: var(--focus-ring-width) solid var(--color-focus-ring);
          outline-offset: calc(-1 * var(--focus-ring-width));
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: unifyGrey[100],
            color: '{primary.400}', // #2B6B7B (was failing mainTeal #338498, 4.29:1)
          },
          option: {
            focusBackground: unifyGrey[200], // distinct from the white overlay; the ring above is the primary cue
          },
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
            placeholderColor: unifyBlueGrey[200],
            borderColor: unifyBlue[500],
          },
          overlay: {
            background: unifyBlueGrey[400],
            color: unifyColors.white,
          },
        },
      },
    },
    paginator: {
      css: `
        .p-paginator {
          border-top-left-radius: 0 !important;
          border-top-right-radius: 0 !important;
          border-bottom-left-radius: 4px !important;
          border-bottom-right-radius: 4px !important;
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: unifyGrey[100],
          },
        },
        dark: {
          root: {
            background: unifyTeal[400],
            color: unifyColors.white,
            borderRadius: '0',
          },
        },
      },
    },
    checkbox: {
      css: `
        .p-checkbox-box {
          border-radius: 4px !important;
          width: 20px !important;
          height: 20px !important;
          border-width: 2px !important;
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: unifyColors.white,
            borderColor: '{primary.400}', // #2B6B7B unchecked border (matrix 1A, 6.01:1)
            checkedBackground: '{teal.700}', // #3D8B8F — white check = 3.97:1 (matrix non-text 3:1)
            checkedBorderColor: '{teal.700}',
            checkedHoverBackground: '{teal.800}',
            checkedHoverBorderColor: '{teal.800}',
          },
          icon: {
            checkedColor: unifyColors.white,
            checkedHoverColor: unifyColors.white,
          },
        },
      },
    },
    radiobutton: {
      css: `
        /* Circular box + dot so single-select reads as a radio, not a checkbox (WCAG 1.3.3). */
        .p-radiobutton-box {
          border-radius: 9999px !important;
          width: 20px !important;
          height: 20px !important;
          border-width: 2px !important;
          background: white !important;
        }
        .p-radiobutton-box .p-radiobutton-icon {
          border-radius: 9999px !important;
          width: 12px !important;
          height: 12px !important;
          background-color: ${unifyTeal[700]} !important;
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: unifyColors.white,
            borderColor: '{primary.400}', // #2B6B7B border (matrix)
            checkedBorderColor: '{teal.700}', // #3D8B8F
          },
          icon: {
            checkedColor: '{teal.700}', // #3D8B8F dot
          },
        },
      },
    },
    select: {
      // Keyboard navigation in the overlay moves a virtual focus (.p-focus) between options. Aura
      // only signals it with option.focusBackground ({surface.100} = grey/100), which is identical
      // to our overlay background, so the focused option was invisible (WCAG 2.4.7). Draw the
      // single focus ring inset on the option so it can't be clipped by the overlay edges.
      css: `
        .p-select-option.p-focus {
          outline: var(--focus-ring-width) solid var(--color-focus-ring);
          outline-offset: calc(-1 * var(--focus-ring-width));
        }
      `,
      colorScheme: {
        light: {
          root: {
            background: unifyGrey[100],
            color: '{primary.400}', // #2B6B7B (was failing mainTeal #338498, 4.29:1)
          },
          overlay: {
            background: unifyGrey[100],
            color: '{primary.400}', // #2B6B7B (was failing mainTeal #338498, 4.29:1)
          },
          option: {
            focusBackground: unifyGrey[200], // distinct from the grey/100 overlay; the ring above is the primary cue
          },
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
            placeholderColor: unifyBlueGrey[200],
            borderColor: unifyBlue[500],
          },
          overlay: {
            background: unifyBlueGrey[400],
            color: unifyColors.white,
          },
        },
      },
    },
    // tabs: intentionally NOT overridden. Account.vue is the only consumer; Aura's defaults
    // resolve to the matrix light tabs (white tablist, #525252 muted tabs, #2B6B7B active +
    // activeBar, navy focus ring) once primary/surface/text are set above.
    tag: {
      colorScheme: {
        light: {
          primary: {
            background: unifyGrey[50], // Light gray background
            color: unifyColors.type.primary, // Dark gray text
          },
          secondary: {
            background: unifyGrey[100], // Light gray background
            color: unifyGrey[500], // Dark gray text
          },
          root: {},
        },
        dark: {
          primary: {
            background: unifyGrey[400], // Darker gray for dark mode
            color: unifyGrey[100], // Light gray text
          },
          secondary: {
            background: unifyGrey[400], // Darker gray for dark mode
            color: unifyGrey[100], // Light gray text
          },
          root: {},
        },
      },
    },
    toast: {
      colorScheme: {
        light: {
          root: {},
        },
        dark: {
          root: {},
          info: {
            borderColor: unifyBlue[500],
          },
        },
      },
    },
    togglebutton: {
      root: {
        borderRadius: '{border.radius.lg}', // 8px to match the button family (Figma radius/lg)
      },
      colorScheme: {
        light: {
          root: {
            background: unifyGrey[100],
            color: unifyGrey[600],
            borderColor: 'transparent',
            hoverBackground: unifyGrey[100],
            checkedColor: unifyBlue[500],
          },
        },
        dark: {
          root: {
            background: unifyBlueGrey[500],
            color: unifyColors.white,
            borderColor: unifyBlueGrey[200],
            hoverBackground: unifyBlueGrey[400],
          },
        },
      },
    },
    toolbar: {
      colorScheme: {
        light: {
          root: {
            background: unifyColors.mainGrey,
            color: '{primary.400}', // #2B6B7B (was failing mainTeal #338498, 4.29:1)
          },
        },
        dark: {
          root: {
            background: unifyTeal[500],
            color: unifyColors.white,
          },
        },
      },
    },
  },
});
